import { MainPropertyType, isUndefined, schTkv2PairedCostSettings } from '@aloft/toolkit-shared';
import { Flex } from '@aloft/toolkit-sui';

import React from 'react';

import {
  selectAdjustmentFeatures,
  selectCompAdjustments,
} from '../../redux/features/selectors/adjustmentSelectors.ts';
import {
  selectAdjustmentSettings,
  selectCompSequence,
  selectOrder,
} from '../../redux/features/selectors/orderSelectors.ts';
import { selectPropertyDataEntities } from '../../redux/features/selectors/propertyDataSelectors/propertyDataSelectors.ts';
import { selectAivArvActiveTab } from '../../redux/features/selectors/uiSelectors.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { CompPropertyDataState } from '../../redux/types.ts';
import { PairedCostAdjustmentSummaryTable } from '../summaryV2/PairedCostAdjustmentSummary/PairedCostAdjustmentSummaryTable.tsx';
import { AdjustmentsModalContent } from './AdjustmentsModalContent.tsx';
import { AdjustmentsToolbar } from './components/AdjustmentsToolbar.tsx';
import { ADJUSTMENT_LABELS } from './consts.ts';
import { AdjustmentsModuleTitle } from './internal/AdjustmentsModuleTitle.tsx';
import { selectSubjectPropertyType } from '../../redux/features/selectors/selectSubjectPropertyType.ts';

const _AdjustmentsModule = () => {
  const [
    compSequence,
    subject,
    comps,
    adjustmentSettings,
    mainPropertyType,
    { hasArvOption },
    activeTab,
    propertyDataEntities,
  ] = [
      useAppSelector(selectCompSequence),
      useAppSelector((state) => selectAdjustmentFeatures(state, 'subject')),
      useAppSelector(selectCompAdjustments),
      useAppSelector(selectAdjustmentSettings),
      useAppSelector(selectSubjectPropertyType),
      useAppSelector(selectOrder),
      useAppSelector(selectAivArvActiveTab),
      useAppSelector(selectPropertyDataEntities),
    ];

  if (
    isUndefined(subject) ||
    isUndefined(comps) ||
    isUndefined(adjustmentSettings) ||
    isUndefined(mainPropertyType)
  ) {
    return null;
  }

  // Filter compSequence based on active tab when ARV option is available
  const filteredCompSequence = hasArvOption
    ? compSequence.filter((compId) => {
      if (activeTab === 'all') return true;
      const compData = propertyDataEntities[compId] as CompPropertyDataState | undefined;
      if (!compData) return false;
      return compData.compData.compType === activeTab;
    })
    : compSequence;

  // We want to use the order / subject property type here since the
  // adjustment table is defined for all properties from subject -> comps
  const propertyTypeSet = new Set<MainPropertyType>([mainPropertyType]);

  return (
    <Flex height="100%" width="100%" flexDirection="column" paddingLeft="1.5rem">
      <AdjustmentsToolbar />
      <AdjustmentsModuleTitle />
      <PairedCostAdjustmentSummaryTable
        pairedCostVersion="2.0"
        renderModalContent={(adjType) => <AdjustmentsModalContent adjType={adjType} />}
        compSequence={filteredCompSequence}
        subject={subject}
        comps={comps}
        adjustmentLabels={ADJUSTMENT_LABELS}
        pairedCostSettings={schTkv2PairedCostSettings.parse(adjustmentSettings)}
        savePairedCostSettings={async () => {
          // Unused
        }}
        propertyTypeSet={propertyTypeSet}
      />
    </Flex>
  );
};

export const AdjustmentsModule = React.memo(_AdjustmentsModule);
